import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { subscriptionsAPI } from '@/services/api';
import { toast } from 'sonner';
import { User, Mail, CreditCard, Shield, Lock, Calendar, DollarSign, ShoppingCart, Heart, Crown, ShoppingBag, Edit, Trash2, AlertTriangle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Helmet } from 'react-helmet';
import SubscriptionDialog from '@/components/Subscription/SubscriptionDialog';
import { db } from '@/config/firebase';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const Profile = () => {
  const { userProfile, refreshProfile, currentUser } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendCooldownTime, setResendCooldownTime] = useState(null);

  // Update displayName when userProfile changes
  React.useEffect(() => {
    if (userProfile?.displayName) {
      setDisplayName(userProfile.displayName);
    }
  }, [userProfile]);
  
  // Check for resend cooldown
  React.useEffect(() => {
    const cooldownUntil = localStorage.getItem('emailResendCooldownUntil');
    if (cooldownUntil) {
      const cooldownDate = new Date(cooldownUntil);
      if (cooldownDate > new Date()) {
        setResendCooldownTime(cooldownDate);
      } else {
        localStorage.removeItem('emailResendCooldownUntil');
      }
    }
  }, []);
  
  // Countdown timer for cooldown
  React.useEffect(() => {
    if (!resendCooldownTime) return;

    const interval = setInterval(() => {
      if (new Date() >= resendCooldownTime) {
        setResendCooldownTime(null);
        localStorage.removeItem('emailResendCooldownUntil');
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCooldownTime]);
  
  const handleResendVerificationEmail = async () => {
    if (resendCooldownTime) return;

    setIsResendingEmail(true);
    try {
      const functions = getFunctions();
      const resendEmail = httpsCallable(functions, 'apiResendEmailVerification');
      const result = await resendEmail();

      if (result.data.success) {
        toast.success('Verification email sent! Please check your inbox.');
        
        // Set 10-minute cooldown
        const cooldownUntil = new Date(Date.now() + 10 * 60 * 1000);
        localStorage.setItem('emailResendCooldownUntil', cooldownUntil.toISOString());
        setResendCooldownTime(cooldownUntil);
      } else if (result.data.alreadyVerified) {
        toast.info('Your email is already verified!');
        // Refresh to update UI
        await refreshProfile();
      } else if (result.data.rateLimited) {
        toast.error(`Please wait ${result.data.waitMinutes} minutes before requesting another email.`);
      } else {
        toast.error(result.data.message || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setIsResendingEmail(false);
    }
  };
  
  const getRemainingCooldownTime = () => {
    if (!resendCooldownTime) return null;
    const diff = resendCooldownTime - new Date();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      // Update directly in Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName,
        updatedAt: new Date().toISOString(),
      });
      await refreshProfile();
      toast.success('Profile updated successfully');
      setIsEditing(false); // Exit edit mode after successful update
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      await subscriptionsAPI.cancel();
      await refreshProfile();
      toast.success('Subscription cancelled');
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel subscription');
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      // Delete user document from Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await deleteDoc(userRef);
      
      // Delete Firebase Auth account
      await currentUser.delete();
      
      toast.success('Account deleted successfully');
      // User will be automatically redirected by auth state change
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error('Failed to delete account. Please try again or contact support.');
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>User Profile - Manage Your AI T-Shirt Design Account | Fluxara</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 p-6 gradient-mesh">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2 text-gray-900">User Profile</h1>
            <p className="text-gray-600">Manage your account settings and view your activity</p>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Email Verification Warning */}
            {currentUser && !currentUser.emailVerified && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                      Email Verification Required
                    </h3>
                    <p className="text-sm text-yellow-800 mb-4">
                      Your email address is not verified yet. Please verify your email to receive your{' '}
                      <span className="font-bold">100 FREE credits</span> and unlock full access to all features.
                    </p>
                    <Button
                      onClick={handleResendVerificationEmail}
                      disabled={isResendingEmail || !!resendCooldownTime}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                      size="sm"
                    >
                      {isResendingEmail 
                        ? 'Sending...' 
                        : resendCooldownTime 
                        ? `Wait ${getRemainingCooldownTime()}` 
                        : 'Resend Verification Email'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Profile Information */}
            <Card className="shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-cyan-600" />
                    <CardTitle>Profile Information</CardTitle>
                  </div>
                  {!isEditing && (
                    <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-gray-700">
                      <User className="w-5 h-5 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Full Name</p>
                        {isEditing ? (
                          <Input
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Your name"
                            className="mt-1"
                            data-testid="display-name-input"
                          />
                        ) : (
                          <p className="font-medium">{displayName || 'Not set'}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 text-gray-700">
                      <Mail className="w-5 h-5 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{userProfile?.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-gray-700">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-500">Member Since</p>
                        <p className="font-medium">
                          {userProfile?.createdAt
                            ? new Date(userProfile.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
                            : 'Recently joined'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {isEditing && (
                    <div className="flex gap-2">
                      <Button 
                        type="submit" 
                        disabled={updating} 
                        className="bg-cyan-600 hover:bg-cyan-700"
                        data-testid="update-profile-button"
                      >
                        {updating ? 'Updating...' : 'Save Changes'}
                      </Button>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          setDisplayName(userProfile?.displayName || '');
                        }}
                        disabled={updating}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>

            {/* Security Settings */}
            <Card className="shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-600" />
                  <CardTitle>Security Settings</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-gray-700">
                    <Lock className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Password Security</p>
                      <p className="font-medium">Your password was last updated recently</p>
                    </div>
                  </div>
                  
                  <Button variant="outline">
                    Change Password
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Account Summary */}
            <Card className="shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-cyan-600" />
                  <CardTitle>Account Summary</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                    <CreditCard className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-1">Available Credits</p>
                    <p className="text-3xl font-bold text-green-600">{userProfile?.credits || 0}</p>
                  </div>
                  
                  <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
                    <ShoppingCart className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-1">Total Purchases</p>
                    <p className="text-3xl font-bold text-blue-600">0</p>
                  </div>
                  
                  <div className="text-center p-6 bg-gradient-to-br from-cyan-50 to-turquoise-50 rounded-xl">
                    <DollarSign className="w-8 h-8 text-cyan-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-600 mb-1">Total Spent</p>
                    <p className="text-3xl font-bold text-cyan-600">$0.00</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subscription & Invoices */}
            <Card className="shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-cyan-600" />
                  <CardTitle>Subscription & Invoices</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-6 bg-gradient-to-r from-cyan-50 to-turquoise-50 rounded-xl">
                    <div className="flex items-center gap-4">
                      <Crown className="w-12 h-12 text-cyan-600" />
                      <div>
                        <p className="font-semibold text-gray-900">
                          {userProfile?.plan === 'free' ? 'No Active Subscription' : `${userProfile?.plan} Plan`}
                        </p>
                        {userProfile?.plan === 'free' && (
                          <p className="text-sm text-gray-600 mt-1">
                            Upgrade to a subscription plan for monthly credits and premium features
                          </p>
                        )}
                        {userProfile?.plan !== 'free' && (
                          <Badge className="mt-1 bg-cyan-600 capitalize">
                            {userProfile?.plan}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowSubscription(true)}
                      className="bg-gradient-to-r from-cyan-600 to-turquoise-600 hover:from-cyan-700 hover:to-turquoise-700"
                      data-testid="manage-subscription-button"
                    >
                      <Crown className="mr-2 w-4 h-4" />
                      {userProfile?.plan === 'free' ? 'View Subscription Plans' : 'Manage Subscription'}
                    </Button>
                    {userProfile?.plan !== 'free' && (
                      <Button
                        variant="outline"
                        onClick={handleCancelSubscription}
                        data-testid="cancel-subscription-button"
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Purchase History */}
            <Card className="shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-cyan-600" />
                  <CardTitle>Purchase History</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No purchases yet</p>
                </div>
              </CardContent>
            </Card>

            {/* Delete Account */}
            <Card className="shadow-lg border-red-200">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <CardTitle className="text-red-600">Danger Zone</CardTitle>
                </div>
                <CardDescription>
                  Permanently delete your account and all associated data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-red-800">
                        <p className="font-semibold mb-1">Warning: This action cannot be undone</p>
                        <p>Deleting your account will permanently remove:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Your profile and account information</li>
                          <li>All your designs and projects</li>
                          <li>Your subscription and credits</li>
                          <li>Purchase history and invoices</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    variant="destructive" 
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <SubscriptionDialog
        open={showSubscription}
        onClose={() => setShowSubscription(false)}
        onSuccess={() => {
          setShowSubscription(false);
          refreshProfile();
        }}
      />

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p className="font-semibold text-gray-900">
                Are you absolutely sure you want to delete your account?
              </p>
              <p>
                This action is permanent and cannot be reversed. All your data will be lost forever.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? 'Deleting...' : 'Yes, Delete My Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Profile;